import { css } from "styled-components";

/**
 * Motion tokens.
 *
 * There was no motion token before this file: 118 `transition` declarations
 * across the app used **33 distinct hand-written values** — `all 0.2s
 * ease-in-out`, `0.2s all ease-in-out` (same thing, arguments swapped), `0.2s`,
 * `0.4s`, `0.01s`, and so on. Nothing told anyone which to reach for, so the
 * interface moves at a different speed depending on which component you happen
 * to be pointing at.
 *
 * The values come from DESIGN.md, which is binding:
 *
 *     Easing:   enter(ease-out) exit(ease-in) move(ease-in-out)
 *     Duration: micro  50-100ms  (hover states, toggles)
 *               short  150-250ms (button feedback, tab switches)
 *               medium 250-400ms (panel expand/collapse, modal enter/exit)
 *
 * Note what that means for hover: the app's dominant value was 200ms, with eight
 * sites at 400ms — two to four times the mandate, and 400ms is the bucket for a
 * modal opening. That is the single biggest reason hovers felt sluggish.
 */
export const DURATION = {
  /** Hover, toggles, colour swaps. Fast enough to feel like a response. */
  micro: "80ms",
  /** Button feedback, tab switches. */
  short: "200ms",
  /** Panels expanding, modals entering. */
  medium: "300ms",
} as const;

export const EASING = {
  /** Something appearing. */
  enter: "ease-out",
  /** Something leaving. */
  exit: "ease-in",
  /** Something already on screen changing — the common case for hover. */
  move: "ease-in-out",
} as const;

/**
 * The transition a hover should use.
 *
 * Prefer naming the properties over `all`: `all` animates layout properties too,
 * so a hover that changes only a colour can end up animating width or padding on
 * an unrelated state change.
 *
 *     &:hover { color: ...; }
 *     ${transition("color")}
 */
export const transition = (
  properties: string,
  duration: string = DURATION.micro,
  easing: string = EASING.move
) => css`
  transition: ${properties} ${duration} ${easing};
`;

/** Hover feedback for an interactive surface: colour and background together. */
export const HOVER_TRANSITION = css`
  transition: color ${DURATION.micro} ${EASING.move},
    background-color ${DURATION.micro} ${EASING.move},
    border-color ${DURATION.micro} ${EASING.move},
    opacity ${DURATION.micro} ${EASING.move};
`;
